import {
  ExperimentDiagnosticsMetricType,
  ExperimentDiagnosticsPoint,
  ExperimentDiagnosticsSeries,
} from "@/http/api-types";

export type DiagnosticsCheckType = ExperimentDiagnosticsMetricType | "daily_spend";
export const DAILY_SPEND_ISSUE_THRESHOLD_PCT = 1;

export const DIAGNOSTIC_CHECKS: {
  metricType: DiagnosticsCheckType;
  title: string;
  chartTitle: string;
  description: string;
}[] = [
  {
    metricType: "bids",
    title: "All variants are bidding",
    chartTitle: "Bids by Variant",
    description: "Every diagnostics run should show more than 0 bids for each variant.",
  },
  {
    metricType: "impressions",
    title: "All variants are receiving impressions",
    chartTitle: "Impressions by Variant",
    description: "Every diagnostics run should show more than 0 impressions for each variant.",
  },
  {
    metricType: "revenue_eur",
    title: "All variants are generating revenue",
    chartTitle: "Revenue by Variant",
    description: "Every diagnostics run should show more than 0 revenue for each variant.",
  },
  {
    metricType: "daily_spend",
    title: "Daily spend is on track",
    chartTitle: "Daily spend by variant",
    description:
      "Completed days compare actual spend against saved targets for global experiments, and relative variant spend for custom experiments.",
  },
];

export function getDiagnosticsSeriesKey(series: ExperimentDiagnosticsSeries) {
  return `${series.member_type}:${series.member_id}:${series.variant}`;
}

export function isDiagnosticsIssue(point: ExperimentDiagnosticsPoint) {
  return point.value <= 0;
}

export function isOpenDiagnosticsIssue(point: ExperimentDiagnosticsPoint) {
  return isDiagnosticsIssue(point) && !point.is_acknowledged;
}

export function isAcknowledgedDiagnosticsIssue(point: ExperimentDiagnosticsPoint) {
  return isDiagnosticsIssue(point) && point.is_acknowledged;
}

export function getDiagnosticsCheckSummary(
  hasHistory: boolean,
  openIssueCount: number,
  acknowledgedIssueCount: number,
) {
  if (!hasHistory) {
    return "No diagnostics history yet";
  }
  if (openIssueCount === 0 && acknowledgedIssueCount === 0) {
    return "No active issues";
  }
  if (openIssueCount === 0) {
    return `${acknowledgedIssueCount} acknowledged`;
  }
  if (acknowledgedIssueCount === 0) {
    return `${openIssueCount} open issue${openIssueCount === 1 ? "" : "s"}`;
  }
  return `${openIssueCount} open, ${acknowledgedIssueCount} acknowledged`;
}

export function formatDiagnosticsMetricValue(metricType: ExperimentDiagnosticsMetricType, value: number) {
  if (metricType === "revenue_eur") {
    const maximumFractionDigits = value > 0 && value < 0.01 ? 6 : value > 0 && value < 1 ? 4 : 2;
    const minimumFractionDigits = value > 0 && value < 1 ? 2 : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);
}

const chartDayLabelFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  timeZone: "UTC", // chart rows are UTC day strings; format in UTC so the day never shifts
});

// formatChartDayLabel turns a "YYYY-MM-DD" day string into a readable "Jun 20" label for chart axes
// and tooltips. Shared by the diagnostics daily-spend and Vertex-runs charts.
export function formatChartDayLabel(date: string) {
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? date : chartDayLabelFormatter.format(parsed);
}

export function isDailySpendIssue(actualSpend: number, targetSpend?: number) {
  if (!targetSpend || targetSpend <= 0) {
    return false;
  }

  return Math.abs(((actualSpend - targetSpend) / targetSpend) * 100) > DAILY_SPEND_ISSUE_THRESHOLD_PCT;
}
